import { fromAbsolute, getLocalTimeZone, ZonedDateTime } from '@internationalized/date';
import {
  Box,
  Code,
  DatePicker,
  Form,
  FormLayout,
  Icon,
  InlineStack,
  Link,
  Paragraph,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  TextField,
  TextLabel,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { zonedDateTimeToUnixSeconds } from '@customerio/pluma-components/react/utils/date-time';
import { useWatch, useZodForm } from '@/components/form';
import type { ObjectType } from '@/services/db/object-type/entity';
import { epochNowInSeconds } from '@/utils/time';
import type { SqlImportDraft } from '../draft';
import {
  INTERVAL_UNITS,
  intervalUnitOptions,
  PEOPLE_ID_FIELDS,
  PEOPLE_TARGET,
  defaultIdFieldName,
  settingsFormDefaults,
  settingsFormSchema,
  settingsToDraft,
  type IntervalUnit,
  type SettingsFormValues,
} from '../settings';
import { SegmentFields } from './segment-fields';

export const SETTINGS_FORM_ID = 'sql-import-settings-form';

const SUPPRESSION_DOCS_URL = 'https://docs.customer.io/journeys/deleting-users/';

type SettingsForm = ReturnType<typeof useZodForm<SettingsFormValues>>;
type Update = (patch: Partial<SettingsFormValues>) => void;

function toIntervalUnit(value: string | null): IntervalUnit {
  return INTERVAL_UNITS.find((unit) => unit === value) ?? 'minute';
}

type IntervalFieldsProps = {
  form: SettingsForm;
  isReadOnly: boolean;
  update: Update;
};

function IntervalFields({ form, isReadOnly, update }: IntervalFieldsProps) {
  const count = useWatch({ control: form.control, name: 'intervalCount' });
  const unit = useWatch({ control: form.control, name: 'intervalUnit' });
  const error = form.formState.errors.intervalCount?.message;
  return (
    <Box>
      <TextLabel id="interval-label">How often should this import sync?</TextLabel>
      <InlineStack alignBlock="center" gap="150" mt="050">
        <Text>Every</Text>
        <Box style={{ width: '5rem' }}>
          <TextField
            ariaLabel="Interval amount"
            type="number"
            min={1}
            value={count}
            error={error}
            isDisabled={isReadOnly}
            data-test-create-sql-import-input="interval-count"
            onInput={(event) => {
              if (event.target instanceof HTMLInputElement) {
                update({ intervalCount: event.target.value });
              }
            }}
          />
        </Box>
        <Select
          ariaLabel="Interval unit"
          options={intervalUnitOptions(Number(count))}
          value={unit}
          isDisabled={isReadOnly}
          data-test-create-sql-import-select="interval-unit"
          onChange={(value) => {
            const next = toIntervalUnit(value);
            if (next !== unit) {
              update({ intervalUnit: next });
            }
          }}
        />
      </InlineStack>
    </Box>
  );
}

type ScheduleFieldProps = {
  form: SettingsForm;
  isReadOnly: boolean;
  update: Update;
};

function ScheduleField({ form, isReadOnly, update }: ScheduleFieldProps) {
  const startAt = useWatch({ control: form.control, name: 'startAt' });
  return (
    <Box style={{ width: '325px' }}>
      <DatePicker
        withTime
        isClearable
        size="sm"
        label="Schedule start time (optional)"
        placeholder="Choose date and time"
        value={startAt === 0 ? null : fromAbsolute(startAt * 1000, getLocalTimeZone())}
        error={form.formState.errors.startAt?.message}
        isDisabled={isReadOnly}
        data-test-create-sql-import-schedule-start-time
        onChange={(value) => {
          update({ startAt: value instanceof ZonedDateTime ? zonedDateTimeToUnixSeconds(value) : 0 });
        }}
        onClear={() => update({ startAt: 0 })}
      />
    </Box>
  );
}

type SyncTargetFieldProps = {
  form: SettingsForm;
  objectTypes: readonly Pick<ObjectType, 'id' | 'name'>[];
  canIdentifyByEmail: boolean;
  isReadOnly: boolean;
  update: Update;
};

/**
 * People, or one of the workspace's object types. Switching resets the identifier and the segment;
 * back to people, the identifier starts over (or stays id where email is not an option).
 */
function SyncTargetField({ form, objectTypes, canIdentifyByEmail, isReadOnly, update }: SyncTargetFieldProps) {
  const target = useWatch({ control: form.control, name: 'syncTarget' });
  return (
    <RadioGroup
      label="Choose what to sync"
      name="sync_target"
      value={target}
      isDisabled={isReadOnly}
      onChange={(value) => {
        if (value === target) {
          return;
        }
        update(
          value === PEOPLE_TARGET
            ? { syncTarget: value, idFieldName: defaultIdFieldName(undefined, canIdentifyByEmail) }
            : { syncTarget: value, idFieldName: '', syncToSegment: false, segmentId: '', segmentName: '' },
        );
      }}
    >
      <Stack gap="100">
        <Box>
          <Radio value={PEOPLE_TARGET} label="People" data-test-create-sql-import-sync-target="people" />
          <Paragraph color="subtle" ml="400">
            An <Code>email</Code> or <Code>id</Code> is required to create new people.
          </Paragraph>
        </Box>
        {objectTypes.map((objectType) => (
          <Box key={objectType.id}>
            <Radio
              value={objectType.id}
              label={objectType.name}
              data-test-create-sql-import-sync-target={objectType.id}
            />
            <Paragraph color="subtle" ml="400">
              An <Code>object_id</Code> is required.
            </Paragraph>
          </Box>
        ))}
      </Stack>
    </RadioGroup>
  );
}

type IdentifyPeopleFieldProps = {
  form: SettingsForm;
  canIdentifyByEmail: boolean;
  isReadOnly: boolean;
  update: Update;
};

/** The people identifier. A workspace that cannot identify by email has one choice, so the field is fixed. */
function IdentifyPeopleField({ form, canIdentifyByEmail, isReadOnly, update }: IdentifyPeopleFieldProps) {
  const idFieldName = useWatch({ control: form.control, name: 'idFieldName' });
  const label = 'How do you want to identify people?';
  return (
    <Tooltip isDisabled={canIdentifyByEmail} isCustom={true}>
      {(context) => (
        <>
          <Select
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            label={label}
            placeholder="Select"
            options={PEOPLE_ID_FIELDS}
            value={idFieldName === '' ? null : idFieldName}
            error={form.formState.errors.idFieldName?.message}
            isDisabled={isReadOnly || !canIdentifyByEmail}
            data-test-create-sql-import-select="update-people-by"
            onChange={(value) => {
              if ((value ?? '') !== idFieldName) {
                update({ idFieldName: value ?? '' });
              }
            }}
          />
          <TooltipContent>
            <Paragraph color="inherit">
              <Code>id</Code> will be used to match SQL rows to people.
            </Paragraph>
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

/** A yes/no radio group's change, ignoring the group echoing its current value. */
function onBooleanChange(current: boolean, next: string, onChange: (value: boolean) => void) {
  const value = next === 'true';
  if (value !== current) {
    onChange(value);
  }
}

type BooleanChoiceProps = {
  value: boolean;
  isReadOnly: boolean;
  onChange: (value: boolean) => void;
};

function EmptyValuesField({ value, isReadOnly, onChange }: BooleanChoiceProps) {
  return (
    <RadioGroup
      label="What should we do with empty values?"
      name="clear_empty_cells"
      value={value ? 'true' : 'false'}
      isDisabled={isReadOnly}
      onChange={(next) => onBooleanChange(value, next, onChange)}
    >
      <Radio value="true" label="Ignore them" data-test-create-sql-import-clear-empty-cells="true" />
      <Radio
        value="false"
        label="Delete existing attribute value"
        data-test-create-sql-import-clear-empty-cells="false"
      />
    </RadioGroup>
  );
}

function SuppressedPeopleField({ value, isReadOnly, onChange }: BooleanChoiceProps) {
  return (
    <Box>
      <InlineStack alignBlock="center" gap="050">
        <TextLabel id="suppressed-people-label">What should we do with suppressed people?</TextLabel>
        <Tooltip isCustom={true}>
          {(context) => (
            <>
              <Icon
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                name="info"
                size="sm"
                aria-label="About suppression"
              />
              <TooltipContent>
                <Paragraph color="inherit">
                  Suppressing a person redacts activity attributed to the person and prevents you from adding a person
                  with the same identifiers to your workspace in the future. This is typically used for GDPR compliance.
                </Paragraph>
              </TooltipContent>
            </>
          )}
        </Tooltip>
        {/* Outside the tooltip on purpose: Pluma tooltips hold static content only, so an
            interactive link needs its own place beside the label. */}
        <Link href={SUPPRESSION_DOCS_URL} variant="secondary" isExternal>
          Learn more
        </Link>
      </InlineStack>
      <RadioGroup
        ariaLabelledby="suppressed-people-label"
        name="ignore_suppress_err"
        value={value ? 'true' : 'false'}
        isDisabled={isReadOnly}
        onChange={(next) => onBooleanChange(value, next, onChange)}
      >
        <Radio
          value="true"
          label="Ignore them and mark the sync successful"
          data-test-sql-import-ignore-suppress-error="true"
        />
        <Radio
          value="false"
          label="Ignore them and mark the sync unsuccessful"
          data-test-sql-import-ignore-suppress-error="false"
        />
      </RadioGroup>
    </Box>
  );
}

type PeopleFieldsProps = {
  form: SettingsForm;
  workspaceId: number;
  canIdentifyByEmail: boolean;
  canCreateSegment: boolean;
  isReadOnly: boolean;
  /** The identifier cannot change on an active import; see `isTargetLocked`. */
  isIdentifierLocked?: boolean;
  update: Update;
};

/** What only a people import asks: how to identify them, and whether to fill a segment. */
function PeopleFields({
  form,
  workspaceId,
  canIdentifyByEmail,
  canCreateSegment,
  isReadOnly,
  isIdentifierLocked = false,
  update,
}: PeopleFieldsProps) {
  const [syncToSegment, segmentChoice, segmentName, segmentId] = useWatch({
    control: form.control,
    name: ['syncToSegment', 'segmentChoice', 'segmentName', 'segmentId'],
  });
  return (
    <>
      <IdentifyPeopleField
        form={form}
        canIdentifyByEmail={canIdentifyByEmail}
        isReadOnly={isReadOnly || isIdentifierLocked}
        update={update}
      />
      <SegmentFields
        workspaceId={workspaceId}
        values={{ syncToSegment, segmentChoice, segmentName, segmentId }}
        errors={{
          segmentName: form.formState.errors.segmentName?.message,
          segmentId: form.formState.errors.segmentId?.message,
        }}
        canCreateSegment={canCreateSegment}
        isReadOnly={isReadOnly}
        onChange={update}
      />
    </>
  );
}

type SettingsStepProps = {
  workspaceId: number;
  draft: SqlImportDraft;
  objectTypes: readonly Pick<ObjectType, 'id' | 'name'>[];
  canIdentifyByEmail: boolean;
  canCreateSegment: boolean;
  isReadOnly: boolean;
  onChange: (changes: Partial<SqlImportDraft>) => void;
  /** The step's form validated; the wizard moves on. */
  onSubmit: (values: SettingsFormValues) => void;
  /** The form was submitted with errors, which are now showing under their fields. */
  onInvalid?: () => void;
  /**
   * The sync target and identifier are fixed: the backend refuses to change them on an active
   * (published) import, so the detail's Settings tab shows them read-only with the reason.
   */
  isTargetLocked?: boolean;
};

const TARGET_LOCKED_HINT =
  "What an active sync syncs, and how it identifies it, can't change. Create a new sync to use a different target or identifier.";

/**
 * The Settings step: how often and from when the import runs, what it writes to (people or an
 * object type), how people are identified, an optional segment, and what to do with empty values
 * and suppressed people. Every change is mirrored onto the draft so a header save carries it; the
 * wizard's "Next" submits this form by id, which is when the rules are checked.
 */
export function SettingsStep({
  workspaceId,
  draft,
  objectTypes,
  canIdentifyByEmail,
  canCreateSegment,
  isReadOnly,
  onChange,
  onSubmit,
  onInvalid,
  isTargetLocked = false,
}: SettingsStepProps) {
  const form = useZodForm<SettingsFormValues>({
    schema: settingsFormSchema(epochNowInSeconds),
    mode: 'onSubmit',
    defaultValues: settingsFormDefaults(draft, { canCreateSegment, canIdentifyByEmail }),
  });
  const syncTarget = useWatch({ control: form.control, name: 'syncTarget' });
  const clearEmptyCells = useWatch({ control: form.control, name: 'clearEmptyCells' });
  const ignoreSuppressErr = useWatch({ control: form.control, name: 'ignoreSuppressErr' });

  const update: Update = (patch) => {
    for (const [key, value] of Object.entries(patch)) {
      form.setValue(toFormKey(key), value, { shouldValidate: form.formState.isSubmitted });
    }
    onChange(settingsToDraft(form.getValues()));
  };

  return (
    <Form
      id={SETTINGS_FORM_ID}
      autoLoading={false}
      onSubmit={form.handleSubmit(
        (values) => {
          onChange(settingsToDraft(values));
          onSubmit(values);
        },
        () => onInvalid?.(),
      )}
    >
      <FormLayout>
        <IntervalFields form={form} isReadOnly={isReadOnly} update={update} />
        <ScheduleField form={form} isReadOnly={isReadOnly} update={update} />
        {isTargetLocked ? (
          <Paragraph color="subtle" data-test-sql-import-target-locked>
            {TARGET_LOCKED_HINT}
          </Paragraph>
        ) : null}
        <SyncTargetField
          form={form}
          objectTypes={objectTypes}
          canIdentifyByEmail={canIdentifyByEmail}
          isReadOnly={isReadOnly || isTargetLocked}
          update={update}
        />
        {syncTarget === PEOPLE_TARGET ? (
          <PeopleFields
            form={form}
            workspaceId={workspaceId}
            canIdentifyByEmail={canIdentifyByEmail}
            canCreateSegment={canCreateSegment}
            isReadOnly={isReadOnly}
            isIdentifierLocked={isTargetLocked}
            update={update}
          />
        ) : null}
        <EmptyValuesField
          value={clearEmptyCells}
          isReadOnly={isReadOnly}
          onChange={(value) => update({ clearEmptyCells: value })}
        />
        <SuppressedPeopleField
          value={ignoreSuppressErr}
          isReadOnly={isReadOnly}
          onChange={(value) => update({ ignoreSuppressErr: value })}
        />
      </FormLayout>
    </Form>
  );
}

const FORM_KEYS: readonly (keyof SettingsFormValues)[] = [
  'intervalCount',
  'intervalUnit',
  'startAt',
  'syncTarget',
  'idFieldName',
  'syncToSegment',
  'segmentChoice',
  'segmentName',
  'segmentId',
  'clearEmptyCells',
  'ignoreSuppressErr',
];

function toFormKey(key: string): keyof SettingsFormValues {
  const match = FORM_KEYS.find((candidate) => candidate === key);
  if (match == null) {
    throw new Error(`Unknown settings field: ${key}`);
  }
  return match;
}
